import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Phone, Mail } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", business: "", email: "", phone: "", message: "" };

export default function Consultation() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail
          ? "Please check your details and try again."
          : "Something went wrong. Please try again or call us."
      );
    }
  };

  return (
    <section
      id="book"
      data-testid="consultation-section"
      className="relative py-24 lg:py-32 bg-[#10242B] text-white grain overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-start">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-[#10BFCB]">
              Book a Free Consultation
            </h4>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight">
              Let's tidy up your numbers
            </h2>
            <p className="mt-5 text-lg text-white/80 max-w-lg">
              Tell us a little about your business and we'll get back to you to
              arrange a free, no-obligation 30-minute chat. No jargon, no
              pressure — just honest advice.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="consultation-phone"
                className="flex items-center gap-3 text-white/90 hover:text-[#10BFCB] transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#10BFCB]/15">
                  <Phone size={20} className="text-[#10BFCB]" />
                </span>
                <span className="font-semibold">{PHONE_DISPLAY}</span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="consultation-email"
                className="flex items-center gap-3 text-white/90 hover:text-[#10BFCB] transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#10BFCB]/15">
                  <Mail size={20} className="text-[#10BFCB]" />
                </span>
                <span className="font-semibold">{EMAIL}</span>
              </a>
            </div>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-md p-8 lg:p-10 text-[#10242B]">
            {status === "success" ? (
              <div
                data-testid="consultation-success"
                className="flex flex-col items-center text-center py-10"
              >
                <CheckCircle2 size={56} className="text-[#10BFCB] mb-5" />
                <h3 className="font-heading text-2xl font-semibold text-[#10242B]">
                  Thank you!
                </h3>
                <p className="mt-3 text-[#55636B]">
                  Your request has been received. Annelize will be in touch
                  shortly to arrange your free consultation.
                </p>
                <button
                  data-testid="consultation-reset"
                  onClick={() => setStatus("idle")}
                  className="mt-7 border-2 border-[#10242B] text-[#10242B] font-semibold px-6 py-3 rounded-md hover:bg-[#10242B] hover:text-white transition-colors"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="consultation-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    placeholder="Your name"
                    testid="input-name"
                  />
                  <Field
                    label="Business"
                    name="business"
                    value={form.business}
                    onChange={onChange}
                    placeholder="Business name"
                    testid="input-business"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="you@email.com"
                    testid="input-email"
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    placeholder="027 000 0000"
                    testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold tracking-wide text-[#10242B] mb-2">
                    How can we help?
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    data-testid="input-message"
                    placeholder="Tell us about your business and what you need..."
                    className="bg-white border-2 border-[#E1E8E9] rounded-md px-4 py-3 focus:border-[#10BFCB] focus:outline-none focus:ring-0 text-base w-full transition-colors resize-none"
                  />
                </div>

                {status === "error" && (
                  <p
                    data-testid="consultation-error"
                    className="text-sm text-red-500 font-medium"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="consultation-submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#E8791F] text-white font-semibold px-6 py-4 rounded-md hover:bg-[#CE6410] transition-colors disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    "Request My Free Consultation"
                  )}
                </button>
                <p className="text-xs text-[#55636B] text-center">
                  We'll never share your details. NZ-wide service.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold tracking-wide text-[#10242B] mb-2">
        {label}
        {props.required && <span className="text-[#0DA2AD]"> *</span>}
      </label>
      <input
        {...props}
        data-testid={testid}
        className="bg-white border-2 border-[#E1E8E9] rounded-md px-4 py-3 focus:border-[#10BFCB] focus:outline-none focus:ring-0 text-base w-full transition-colors"
      />
    </div>
  );
}
